import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import {
  CONTRACT_TYPES, VAT_OPTIONS, DOCUMENT_LANGUAGES, INSPECTION_UNITS, PARTY_TYPES
} from './constants';
import {
  restoreEconState, suggestContractType, PURPOSE_SUGGESTIONS, sanitizeDecimal, sanitizeDigits, withCommas, buildEconPayload, validateEcon, splitRemoteErrors, computeAmounts,
  addMonthsIso, formatBaht, newDetail
} from './payload';
import ContractStatusPanel from './ContractStatusPanel';
import DepartmentSelect from './DepartmentSelect';
import SerialSelect from './SerialSelect';
import { useAuth } from '../../context/AuthContext';
import LoaEditor from './LoaEditor';
import TermsEditor from './TermsEditor';
import { termsToHtml, htmlToTerms } from './terms';

// Fields that have their own inline error slot on this step. A remote error on
// any other key (e.g. one derived from step 1) goes in the banner instead, so
// it's never silently swallowed.
const INLINE_KEYS = new Set([
  'contract_type', 'contract_number', 'poa_number', 'department_code', 'document_language',
  'title', 'subject', 'purpose_text', 'contract_date', 'installation_location_name',
  'customer_signer_job_position', 'lessor_company_name', 'lessor_company_phone_number',
  'lessor_signer_full_name', 'lessor_signer_job_position', 'lessor_company_address',
  'rental_start_date', 'rental_asset_value', 'rental_amount_per_month', 'rental_vat_percent',
  'rental_advance_payment_schedule', 'rental_first_payment_date', 'transport_amount',
  'transport_vat_percent', 'deposit_amount', 'deposit_vat_percent', 'inspection_interval',
  'inspection_interval_unit', 'inspection_interval_text', 'additional_terms', 'loa_detail', 'details'
]);

// Defined at module level (NOT inside EContractSection): a component declared
// in the parent's body is a new component type on every render, which makes
// React remount every input under it — and drop focus — on each keystroke.
// Errors reach it through context for the same reason.
const ErrorsContext = React.createContext({});

const Field = ({ label, name, required, full, hint, children }) => {
  const errors = React.useContext(ErrorsContext);
  return (
    <div className={`rs-field${full ? ' rs-field--full' : ''}`} data-econ-error={errors[name] ? '1' : undefined}>
      <label>{label}{required && <span className="rs-req"> *</span>}</label>
      {children}
      {errors[name] && <div className="econ-field-error">{errors[name]}</div>}
      {hint && <div className="rs-help">{hint}</div>}
    </div>
  );
};

// Section card shared with step 1's look. Module level for the same reason as
// Field: a component declared inside the parent would remount on every render.
const Card = ({ kicker, title, desc, errorKey, action, children }) => {
  const errors = React.useContext(ErrorsContext);
  return (
    <section className="rs-card" data-econ-error={errorKey && errors[errorKey] ? '1' : undefined}>
      <div className="rs-card__head">
        <div className="rs-card__headtext">
          {kicker && <div className="rs-card__kicker">{kicker}</div>}
          <h2 className="rs-card__title">{title}</h2>
          {desc && <p className="rs-card__desc">{desc}</p>}
        </div>
        {action && <div className="rs-card__action">{action}</div>}
      </div>
      {children}
    </section>
  );
};

// Money is typed, shown with thousands separators, and stored as a plain
// number string. Text input (not type=number) so scrolling over it never
// changes the value. The caret is restored after reformatting so typing in
// the middle of a number doesn't jump to the end.
const MoneyInput = ({ name, value, onChange, disabled, hasError }) => {
  const ref = useRef(null);
  const caret = useRef(null);
  useLayoutEffect(() => {
    if (caret.current !== null && ref.current) {
      ref.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  });
  const handle = (e) => {
    const { value: typed, selectionStart } = e.target;
    const significantBefore = typed.slice(0, selectionStart).replace(/[^0-9.]/g, '').length;
    const clean = sanitizeDecimal(typed);
    const shown = withCommas(clean);
    let pos = 0;
    for (let seen = 0; pos < shown.length && seen < significantBefore; pos += 1) {
      if (/[0-9.]/.test(shown[pos])) seen += 1;
    }
    caret.current = pos;
    onChange(name, clean);
  };
  return (
    <div className={`econ-money-field${hasError ? ' econ-money-field--error' : ''}`}>
      <span className="econ-money-field__prefix">฿</span>
      <input ref={ref} type="text" inputMode="decimal" autoComplete="off" name={name}
        value={withCommas(value)} onChange={handle} disabled={disabled} placeholder="0.00" />
    </div>
  );
};

// Fields shown on the main page; any other field with an error lives in the options section.
const MAIN_KEYS = new Set([
  'contract_type', 'contract_number', 'department_code', 'poa_number', 'title', 'purpose_text',
  'rental_amount_per_month', 'deposit_amount', 'transport_amount', 'details', 'customer_company_phone_number'
]);

const PARTY_LABEL = Object.fromEntries(PARTY_TYPES.map(p => [p.value, p.label]));

const scrollToFirstError = () => {
  setTimeout(() => {
    const el = document.querySelector('[data-econ-error="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 80);
};

/**
 * Step 2 of the rental wizard: everything e-Contract needs that step 1
 * doesn't already collect, laid out in the same order as the API's own
 * request body, plus the status panel for a contract that already exists.
 */
// devView: show the request/response log to everyone (UAT testers need it).
// stayOnInvalid: when the host page's own checks fail, stay here instead of jumping back.
// backLabel: text of the back button.
const EContractSection = ({ orderId, order, soData, formData, validateStep1, saveStep1, onBack, devView = false, stayOnInvalid = false, backLabel = 'ย้อนกลับ' }) => {
  const [initial] = useState(() => restoreEconState({
    saved: order.econtract_last_payload,
    formData,
    soData
  }));

  const suggestion = suggestContractType(soData);
  // The product group decides the template, so it is locked to that choice
  // unless the rep deliberately unlocks it.
  const [templateUnlocked, setTemplateUnlocked] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // The developer view (dry-run log, raw payload) is for admins only.
  const auth = useAuth() || {};
  const isAdmin = devView || auth.currentUser?.role === 'admin' || (auth.currentUser?.roles || []).includes('admin');
  const [econForm, setEconForm] = useState(initial.econForm);
  const [loaLevels, setLoaLevels] = useState(initial.loaLevels);
  const [termItems, setTermItems] = useState(() => htmlToTerms(initial.econForm.additional_terms));
  const [details, setDetails] = useState(initial.details);

  const [contractNumber, setContractNumber] = useState(order.econtract_contract_number || null);
  const [snapshot, setSnapshot] = useState(order.econtract_remote_snapshot || null);
  const [syncedAt, setSyncedAt] = useState(order.econtract_synced_at || null);
  const [hasSignedPdf] = useState(Boolean(order.econtract_has_signed_pdf));
  const [renewals, setRenewals] = useState(Array.isArray(order.econtract_renewals) ? order.econtract_renewals : []);
  const [econStatus, setEconStatus] = useState(order.econtract_status || null);
  const [mock, setMock] = useState(false);
  // The system issues the contract number (DC<พ.ศ.>-<MM>-<NNN>); this is only
  // what it will be / already is. Nothing is reserved until the contract is sent.
  const [numberInfo, setNumberInfo] = useState({ number: order.econtract_contract_number || order.econtract_reserved_number || '', issued: Boolean(order.econtract_contract_number || order.econtract_reserved_number) });

  const [errors, setErrors] = useState({});
  const [general, setGeneral] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [netsuiteLog, setNetsuiteLog] = useState([]);
  const [terminalLog, setTerminalLog] = useState(Array.isArray(order.econtract_log) ? order.econtract_log : []);
  const [showLessor, setShowLessor] = useState(false);

  const [poaSearch, setPoaSearch] = useState(initial.econForm.poa_number || '');
  const [poaOptions, setPoaOptions] = useState([]);
  const [poaLoading, setPoaLoading] = useState(false);
  // A POA number restored from a previous save is already chosen — don't run a
  // search (and pop a suggestion list) for it on page load.
  const skipInitialPoaSearch = useRef(Boolean(initial.econForm.poa_number));

  const isUpdate = Boolean(contractNumber);
  const remoteStatus = snapshot?.status || null;
  // e-Contract only accepts writes while a contract is "awaiting".
  const locked = isUpdate && Boolean(remoteStatus) && remoteStatus !== 'awaiting';
  // Product group decided the template: show it as a fact, not a choice.
  const autoTemplate = !templateUnlocked && Boolean(suggestion) && econForm.contract_type === suggestion.type;
  const autoTemplateInfo = autoTemplate ? CONTRACT_TYPES.find(t => t.value === suggestion.type) : null;

  useEffect(() => {
    if (order.econtract_contract_number) return;
    axios.get(`${API_URL}/rental/${orderId}/econtract/next-number`)
      .then(resp => { if (resp.data?.number) setNumberInfo({ number: resp.data.number, issued: Boolean(resp.data.issued) }); })
      .catch(() => {});
  }, [orderId, order.econtract_contract_number]);

  useEffect(() => {
    if (initial.econForm.poa_number) return;
    axios.get(`${API_URL}/econtract/poa-numbers`)
      .then(resp => {
        const list = resp.data?.poaNumbers;
        if (Array.isArray(list) && list.length === 1) {
          skipInitialPoaSearch.current = true;
          selectPoaRef.current(list[0]);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search against the e-Contract POA-numbers endpoint
  useEffect(() => {
    if (skipInitialPoaSearch.current) {
      skipInitialPoaSearch.current = false;
      return undefined;
    }
    // Don't re-search for the value we just picked ("POA… | subject")
    if (!poaSearch || poaSearch.length < 2 || poaSearch.includes(' | ')) {
      setPoaOptions([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setPoaLoading(true);
        const resp = await axios.get(`${API_URL}/econtract/poa-numbers`, {
          params: { search: poaSearch },
          signal: controller.signal
        });
        if (resp.data?.success && Array.isArray(resp.data.poaNumbers)) setPoaOptions(resp.data.poaNumbers);
      } catch (err) {
        if (err.name !== 'CanceledError') console.error('POA number search error:', err);
      } finally {
        setPoaLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [poaSearch]);

  const clearError = (name) => setErrors(prev => {
    if (!prev[name]) return prev;
    const next = { ...prev };
    delete next[name];
    return next;
  });

  const setField = (name, value) => {
    setEconForm(prev => ({ ...prev, [name]: value }));
    clearError(name);
  };
  const onChange = (e) => setField(e.target.name, e.target.value);

  const selectPoaRef = useRef(() => {});
  const selectPoa = (poa) => {
    setPoaSearch(`${poa.poa_number} | ${poa.subject || ''}`.trim());
    setPoaOptions([]);
    setField('poa_number', poa.poa_number);
  };
  selectPoaRef.current = selectPoa;

  const customerName = formData.customer_name || soData?.data?.customerName || '';
  const amounts = computeAmounts(econForm);
  const rentalStart = econForm.rental_start_date || formData.contract_start_date;
  const rentalEnd = addMonthsIso(rentalStart, formData.contract_duration);
  const payload = buildEconPayload({ econForm, loaLevels, details, formData, soData });
  const changeTerms = (items) => { setTermItems(items); setField('additional_terms', termsToHtml(items)); };

  const cls = (name) => `rs-input${errors[name] ? ' rs-input--error rental-form__input--error' : ''}`;

  const text = (name, extra = {}) => (
    <input
      type="text"
      name={name}
      value={econForm[name] ?? ''}
      onChange={onChange}
      disabled={locked}
      className={cls(name)}
      {...extra}
    />
  );

  const money = (name) => (
    <MoneyInput name={name} value={econForm[name]} onChange={setField} disabled={locked} hasError={Boolean(errors[name])} />
  );

  // Whole numbers (day of month, counts): typed digits only, no spinner/wheel.
  const digits = (name, extra = {}) => (
    <input type="text" inputMode="numeric" autoComplete="off" name={name} value={econForm[name] ?? ''}
      onChange={(e) => setField(name, sanitizeDigits(e.target.value))} disabled={locked} className={cls(name)} {...extra} />
  );

  const vatSelect = (name) => (
    <select name={name} value={econForm[name]} onChange={onChange} disabled={locked} className={cls(name)}>
      {VAT_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
    </select>
  );

  const [assetSync, setAssetSync] = useState({ count: null, syncedAt: null, busy: false });
  useEffect(() => {
    axios.get(`${API_URL}/rental/assets`, { params: { limit: 1 } })
      .then(resp => setAssetSync(s => ({ ...s, count: resp.data?.count ?? null, syncedAt: resp.data?.synced_at || null })))
      .catch(() => {});
  }, []);
  const refreshAssets = async () => {
    setAssetSync(s => ({ ...s, busy: true }));
    try {
      const resp = await axios.post(`${API_URL}/rental/assets/refresh`);
      setAssetSync({ count: resp.data.count, syncedAt: resp.data.synced_at, busy: false });
      toast.success(`อัปเดตรายการเครื่อง ${resp.data.count.toLocaleString('th-TH')} เครื่องแล้ว`);
    } catch (err) {
      setAssetSync(s => ({ ...s, busy: false }));
      toast.error(err.response?.data?.message || 'อัปเดตรายการเครื่องไม่สำเร็จ');
    }
  };

  const updateDetail = (index, field, value) => {
    setDetails(prev => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
    clearError('details');
  };

  const showRemoteErrors = (data, hint) => {
    const { fieldErrors, general: detailLines } = splitRemoteErrors(data);
    const inline = {};
    const banner = [];
    Object.entries(fieldErrors).forEach(([key, message]) => {
      if (INLINE_KEYS.has(key)) inline[key] = message;
      else banner.push(`${key}: ${message}`);
    });
    setErrors(inline);
    setGeneral([hint, ...detailLines, ...banner].filter(Boolean));
    scrollToFirstError();
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveStep1();
      await axios.post(`${API_URL}/rental/${orderId}/econtract/draft`, payload);
      toast.success('บันทึกร่างข้อมูลสัญญาแล้ว');
    } catch (error) {
      toast.error('บันทึกร่างไม่สำเร็จ');
    } finally {
      setSavingDraft(false);
    }
  };

  // One click, both systems. NetSuite stays dry-run (a real submit has real,
  // hard-to-undo side effects — it creates an actual sales order); the
  // e-Contract send is real, auto-mocked while ECONTRACT_* env vars are unset.
  const requestSend = () => {
    if (!validateStep1()) {
      if (!stayOnInvalid) onBack();
      return;
    }

    const clientErrors = validateEcon({ econForm, loaLevels, details, formData });
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setGeneral(['ข้อมูลยังไม่ครบ — กรุณาแก้ช่องที่ไฮไลต์สีแดงก่อนส่ง']);
      scrollToFirstError();
      return;
    }
    setErrors({});
    setGeneral([]);
    setReviewOpen(true);
  };

  const confirmSend = async () => {
    setReviewOpen(false);
    setGenerating(true);
    setNetsuiteLog([{
      ts: new Date().toISOString(),
      level: 'info',
      message: `$ POST /rental/${orderId}/submit?dryRun=true  (DRY RUN — ทดสอบเท่านั้น ไม่มีการสร้างจริงใน NetSuite)`
    }]);

    try {
      // Persist step 1 first — the NetSuite step reads rental_orders from the
      // DB, not this request's body.
      await saveStep1();

      // Test orders are synthetic (no real customer in NetSuite), so the NetSuite dry-run is skipped.
      if (!order.is_test) {
        try {
          const nsResponse = await axios.post(`${API_URL}/rental/${orderId}/submit?dryRun=true`);
          setNetsuiteLog(prev => [...prev, {
            ts: new Date().toISOString(),
            level: 'success',
            message: `< 200 OK (DRY RUN — ไม่ได้สร้างจริง)\nPayload ที่จะส่งไป NetSuite ถ้าเป็นการสร้างจริง:\n${JSON.stringify(nsResponse.data.payload, null, 2)}`
          }]);
        } catch (nsError) {
          const nsData = nsError.response?.data;
          setNetsuiteLog(prev => [...prev, {
            ts: new Date().toISOString(),
            level: 'error',
            message: `< ${nsError.response?.status || '???'} Error\n${nsData?.message || nsError.message}${nsData?.missingFields ? `\nMissing fields: ${nsData.missingFields.join(', ')}` : ''}`
          }]);
        }
      }

      try {
        const resp = await axios.post(`${API_URL}/rental/${orderId}/econtract/send`, payload);
        setTerminalLog(resp.data.log || []);
        setMock(Boolean(resp.data.mock));
        setEconStatus('sent');
        setContractNumber(resp.data.data?.contract_number || numberInfo.number);
        if (resp.data.snapshot) {
          setSnapshot(resp.data.snapshot);
          setSyncedAt(new Date().toISOString());
        }
        toast.success(`${isUpdate ? 'อัปเดต' : 'สร้าง'}สัญญาบน e-Contract แล้ว${resp.data.mock ? ' (จำลอง/beta)' : ''}`);
      } catch (sendError) {
        const body = sendError.response?.data;
        if (body?.log) setTerminalLog(body.log);
        setMock(Boolean(body?.mock));
        if (body?.data && typeof body.data === 'object') {
          showRemoteErrors(body.data, body.hint);
        } else {
          setGeneral([body?.message || sendError.message]);
        }
        setEconStatus('error');
        toast.error(body?.local ? 'ข้อมูลไม่ผ่านการตรวจสอบ — ดูช่องที่ไฮไลต์' : (body?.hint || 'ส่งสัญญาไม่สำเร็จ ดู log ด้านล่าง'));
      }
    } catch (error) {
      console.error('Error generating SO + contract:', error);
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setGenerating(false);
    }
  };

  // The common case is one Peerapat approver by email, so that is the only
  // signer field shown up front; any other chain is edited under advanced settings.
  const firstLevel = loaLevels[0];
  const simpleApprover = Boolean(firstLevel && firstLevel.contract_party_type === 'lessor' && firstLevel.user_type === 'user');
  const setApproverEmail = (email) => {
    setLoaLevels(levels => levels.map((l, i) => (i === 0 ? { ...l, members: [{ email }] } : l)));
    clearError('loa_detail');
  };
  const advancedSummary = [
    `VAT ${econForm.rental_vat_percent === 'non vat' ? 'ไม่มี' : `${econForm.rental_vat_percent}%`}`,
    `จ่ายทุกวันที่ ${econForm.rental_advance_payment_schedule || 1}`,
    `ลงวันที่ ${econForm.contract_date || '-'}`
  ].join(' · ');

  const renderLog = (entries, emptyText) => (
    entries.length === 0 ? (
      <div className="rental-form__terminal-empty">{emptyText}</div>
    ) : (
      entries.map((entry, idx) => (
        <div key={idx} className={`rental-form__terminal-line rental-form__terminal-line--${entry.level}`}>
          <span className="rental-form__terminal-ts">[{entry.ts ? new Date(entry.ts).toLocaleTimeString('th-TH') : ''}]</span>
          <span className="rental-form__terminal-msg">{entry.message}</span>
        </div>
      ))
    )
  );

  const hasErrors = Object.keys(errors).length > 0 || general.length > 0;

  // The signers now live in the collapsed options. Anything wrong in there must open
  // it, or the rep would see an error banner pointing at something hidden.
  useEffect(() => {
    if (Object.keys(errors).some(k => !MAIN_KEYS.has(k))) setAdvancedOpen(true);
  }, [errors]);
  // The approver email is required but now out of sight: say so on the closed section.
  const signersIncomplete = Boolean(validateEcon({ econForm, loaLevels, details, formData }).loa_detail);

  return (
    <ErrorsContext.Provider value={errors}>
    <div className="rs-step2">
      <div className="rs-intro">
        <h2 className="rs-intro__title">
          ข้อมูลสัญญาเช่า
          {econStatus === 'error' && <span className="rs-chip rs-chip--red ms-2"><i className="bi bi-exclamation-circle-fill"></i> ส่งไม่สำเร็จ</span>}
        </h2>
        {isUpdate && (
          <p className="rs-intro__text">
            {`โหมดอัปเดต — สัญญานี้ถูกสร้างไว้แล้วในชื่อ "${contractNumber}" การกดส่งครั้งต่อไปจะอัปเดตข้อมูลของสัญญาเดิม (ทำได้เฉพาะขณะสถานะยังเป็น "รอรับงาน")`}
          </p>
        )}
      </div>

      {isUpdate && (
        <ContractStatusPanel
          orderId={orderId}
          contractNumber={contractNumber}
          snapshot={snapshot}
          syncedAt={syncedAt}
          hasSignedPdf={hasSignedPdf}
          renewals={renewals}
          mock={mock || Boolean(snapshot?._mock)}
          onSynced={({ snapshot: s, log, syncedAt: at }) => { setSnapshot(s); setSyncedAt(at); if (log) setTerminalLog(log); }}
          onLog={(log) => log && setTerminalLog(log)}
          onRenewed={(r) => setRenewals(prev => [...prev, r])}
        />
      )}

      {hasErrors && (
        <div className="econ-alert" role="alert">
          <div className="econ-alert__title"><i className="bi bi-exclamation-triangle-fill me-2"></i>ส่งสัญญาไม่ได้ — กรุณาตรวจสอบ</div>
          {general.map((g, i) => <div key={i} className="econ-alert__line">{g}</div>)}
          {Object.keys(errors).length > 0 && (
            <div className="econ-alert__line">มี {Object.keys(errors).length} ช่องที่ต้องแก้ไข (ไฮไลต์สีแดงด้านล่าง)</div>
          )}
        </div>
      )}

      {/* 01 — which contract, who owns it */}
      <Card kicker="01 / CONTRACT" title="สัญญา" errorKey="contract_type">
        <div className="rs-field" style={{ marginBottom: '18px' }}>
          <label>ภาษาของสัญญา</label>
          <div className="econ-segmented econ-segmented--inline">
            {DOCUMENT_LANGUAGES.map(l => (
              <button key={l.value} type="button" disabled={locked}
                className={econForm.document_language === l.value ? 'is-active' : ''}
                onClick={() => setField('document_language', l.value)}>{l.label}</button>
            ))}
          </div>
        </div>

        {autoTemplate ? (
          <div className="rs-auto">
            <div className="rs-auto__body">
              <div className="rs-auto__label">แบบสัญญาเช่า</div>
              <div className="rs-auto__value">{autoTemplateInfo.labelTh} · {autoTemplateInfo.value}</div>
              <div className="rs-auto__sub">กำหนดอัตโนมัติจากกลุ่มสินค้า {suggestion.group} ({autoTemplateInfo.hint})</div>
            </div>
            {!locked && (
              <button type="button" className="econ-link-btn" onClick={() => setTemplateUnlocked(true)}>เปลี่ยนแบบสัญญา</button>
            )}
          </div>
        ) : (
          <div className="rs-field rs-field--full" style={{ marginBottom: '18px' }}>
            <label>แบบสัญญาเช่า <span className="rs-req">*</span></label>
            <div className="econ-type-picker">
              {CONTRACT_TYPES.map(t => (
                <button
                  type="button"
                  key={t.value}
                  disabled={locked}
                  onClick={() => setField('contract_type', t.value)}
                  className={`econ-type-card ${econForm.contract_type === t.value ? 'econ-type-card--selected' : ''}`}
                >
                  <span className="econ-type-card__label">{t.labelTh}</span>
                  {t.hint && <span className="econ-type-card__hint">{t.hint}</span>}
                  <span className="econ-type-card__sub">{t.value}</span>
                </button>
              ))}
            </div>
            {suggestion && econForm.contract_type !== suggestion.type && (
              <div className="econ-field-hint econ-field-hint--warn">
                <i className="bi bi-exclamation-triangle me-1"></i>
                สินค้าในใบสั่งขายอยู่กลุ่ม {suggestion.group} — โดยปกติใช้ {CONTRACT_TYPES.find(t => t.value === suggestion.type)?.labelTh}
              </div>
            )}
            {errors.contract_type && <div className="econ-field-error">{errors.contract_type}</div>}
          </div>
        )}

        <div className="rs-grid">
          <Field label="เลขที่สัญญา" name="contract_number"
            hint={isUpdate || numberInfo.issued ? 'ออกโดยระบบแล้ว' : 'ระบบออกเลขให้เมื่อกดส่ง'}>
            <input type="text" readOnly className="rs-input rs-input--readonly rs-mono-input" name="contract_number"
              value={contractNumber || numberInfo.number || ''} placeholder="กำลังตรวจสอบ..." />
          </Field>
          <Field label="แผนก" name="department_code" required>
            <DepartmentSelect name="department_code" value={econForm.department_code} onChange={setField}
              disabled={locked} hasError={Boolean(errors.department_code)} />
          </Field>
          <Field label="หนังสือมอบอำนาจ" name="poa_number" required full>
            <input
              type="text"
              value={poaSearch}
              onChange={(e) => { setPoaSearch(e.target.value); setEconForm(prev => ({ ...prev, poa_number: '' })); clearError('poa_number'); }}
              placeholder="ค้นหาเลขที่หนังสือมอบอำนาจ..."
              onFocus={(e) => e.target.select()}
              disabled={locked}
              className={cls('poa_number')}
            />
            {econForm.poa_number && <div className="rs-help rs-help--ok"><i className="bi bi-check-circle-fill"></i> เลือกแล้ว: {econForm.poa_number}</div>}
            {(poaLoading || poaOptions.length > 0) && (
              <div className="rs-options">
                {poaLoading && <div className="rs-options__empty">กำลังค้นหา...</div>}
                {!poaLoading && poaOptions.slice(0, 8).map(poa => (
                  <button key={poa.poa_number} type="button" className="rs-option" onClick={() => selectPoa(poa)}>
                    <span className="rs-option__title">{poa.poa_number}</span>
                    <span className="rs-option__meta">{poa.subject} · {poa.start_date} - {poa.end_date}</span>
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>
      </Card>

      {/* 02 — what the contract says */}
      <Card kicker="02 / DETAILS" title="รายละเอียดสัญญา">
        <div className="rs-grid">
          <Field label="ชื่อสัญญา" name="title" required full>
            {text('title', { maxLength: 255, placeholder: 'สัญญาเช่าเครื่อง ...' })}
          </Field>
          <Field label="วัตถุประสงค์การเช่า" name="purpose_text" required full>
            {text('purpose_text', { maxLength: 255, placeholder: 'เลือกหรือพิมพ์เอง', list: 'purpose-suggestions' })}
            <datalist id="purpose-suggestions">
              {PURPOSE_SUGGESTIONS.map(p => <option key={p} value={p} />)}
            </datalist>
          </Field>
        </div>
      </Card>

      {/* 03 — money */}
      <Card kicker="03 / PRICING" title="ค่าเช่าและเงินมัดจำ">
        <div className="rs-grid">
          <Field label="ค่าเช่าต่อเดือน" name="rental_amount_per_month">{money('rental_amount_per_month')}</Field>
          <Field label="เงินมัดจำ" name="deposit_amount">{money('deposit_amount')}</Field>
          <Field label="ค่าขนส่งและติดตั้ง" name="transport_amount">{money('transport_amount')}</Field>
        </div>
        <div className="rs-quote">
          <div><span>ค่าเช่า / เดือน (รวม VAT)</span><strong>฿{formatBaht(amounts.totalRental)}</strong></div>
          <div><span>เงินมัดจำ</span><strong>฿{formatBaht(amounts.totalDeposit)}</strong></div>
          <div><span>ค่าขนส่งและติดตั้ง</span><strong>฿{formatBaht(amounts.totalTransport)}</strong></div>
          <div className="rs-quote__note">ประมาณการ — e-Contract คำนวณยอดจริง</div>
        </div>
      </Card>

      {/* 05 — machines */}
      <Card kicker="04 / MACHINES" title="เครื่องที่เช่า" errorKey="details">
        <div className="rs-machines">
          <div className="rs-machines__row rs-machines__row--head">
            <span>รายการ</span><span>Serial No.</span><span>จำนวน</span><span>หมายเหตุ</span><span></span>
          </div>
          {details.map((d, i) => (
            <div className="rs-machines__row" key={i}>
              <input className="rs-input" value={d.description} disabled={locked} onChange={(e) => updateDetail(i, 'description', e.target.value)} />
              <SerialSelect orderId={orderId} value={d.serial_number} disabled={locked} refreshKey={assetSync.syncedAt}
                onChange={(v) => updateDetail(i, 'serial_number', v)}
                onPick={(a) => setDetails(prev => prev.map((x, idx) => (idx === i ? { ...x, serial_number: a.serial_number, description: a.asset_name || x.description } : x)))} />
              <input className="rs-input" type="text" inputMode="numeric" value={d.quantity} disabled={locked} onChange={(e) => updateDetail(i, 'quantity', sanitizeDigits(e.target.value))} />
              <input className="rs-input" value={d.remark} disabled={locked} onChange={(e) => updateDetail(i, 'remark', e.target.value)} />
              <button type="button" className="econ-icon-btn econ-icon-btn--danger" disabled={locked || details.length < 2} onClick={() => setDetails(details.filter((_, idx) => idx !== i))} title="ลบรายการ">
                <i className="bi bi-trash"></i>
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="econ-link-btn" disabled={locked} onClick={() => setDetails([...details, newDetail({ product_type: details[0]?.product_type || '' })])}>
          <i className="bi bi-plus-circle"></i> เพิ่มเครื่อง
        </button>
        <div className="rs-assetsync">
          <span>
            รายการเครื่องจาก NetSuite{assetSync.count != null ? ` ${assetSync.count.toLocaleString('th-TH')} เครื่อง` : ''}
            {assetSync.syncedAt ? ` · อัปเดตล่าสุด ${new Date(assetSync.syncedAt).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}` : ' · ยังไม่เคยอัปเดต'}
          </span>
          <button type="button" className="econ-link-btn" disabled={assetSync.busy} onClick={refreshAssets}>
            <i className={`bi bi-arrow-clockwise${assetSync.busy ? ' econ-spin' : ''}`}></i> {assetSync.busy ? 'กำลังอัปเดต...' : 'อัปเดตรายการ'}
          </button>
        </div>
        {errors.details && <div className="econ-field-error">{errors.details}</div>}
      </Card>

      {/* Everything with a sensible default lives here, out of the way */}
      <details className="rs-advanced" open={advancedOpen} onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}>
        <summary>
          <span className="rs-advanced__title"><i className="bi bi-three-dots"></i> ผู้ลงนาม และตัวเลือกเพิ่มเติม</span>
          {signersIncomplete
            ? <span className="rs-advanced__sub rs-advanced__sub--warn"><i className="bi bi-exclamation-circle"></i> ต้องกรอกอีเมลผู้อนุมัติฝั่ง Peerapat ก่อนส่ง (เปิดดูที่นี่)</span>
            : <span className="rs-advanced__sub">ผู้ลงนามครบแล้ว · ค่าเริ่มต้น: {advancedSummary}</span>}
        </summary>

        <div className="rs-advanced__body">
          {/* Who signs: the quick answers first, the full chain only for exceptions */}
          <div className="rs-subhead rs-subhead--first">ผู้ลงนามและผู้อนุมัติ</div>
          <p className="rs-help" style={{ margin: '0 0 12px' }}>
            e-Contract ส่งสัญญาให้ลงนามตามลำดับ: <b>1. ผู้อนุมัติฝั่ง Peerapat</b> แล้วจึงถึง <b>2. ผู้ลงนามฝั่งลูกค้า</b>
          </p>
          <div className="rs-signers" data-econ-error={errors.loa_detail ? '1' : undefined}>
            <div className="rs-signers__col">
              <div className="rs-signers__head"><span className="rs-signers__no">1</span> ฝั่ง Peerapat</div>
              {simpleApprover ? (
                <div className="rs-field">
                  <label htmlFor="approver_email">อีเมลผู้อนุมัติ <span className="rs-req">*</span></label>
                  <input id="approver_email" type="email" className={`rs-input${errors.loa_detail ? ' rs-input--error' : ''}`} disabled={locked}
                    placeholder="name@peerapat.com" value={firstLevel.members[0]?.email || ''}
                    onChange={(e) => setApproverEmail(e.target.value)} />
                  <span className="rs-help">ต้องเป็นอีเมลที่มีบัญชีใน e-Contract แล้ว</span>
                </div>
              ) : (
                <p className="rs-help">ลำดับผู้ลงนามถูกปรับแต่งเอง — แก้ไขที่ "ปรับลำดับเอง" ด้านล่าง</p>
              )}
            </div>
            <div className="rs-signers__col">
              <div className="rs-signers__head"><span className="rs-signers__no">2</span> ฝั่งลูกค้า</div>
              <dl className="rs-facts" style={{ margin: '0 0 12px' }}>
                <div><dt>ผู้ลงนาม</dt><dd>{formData.contact_name || '-'} · โทร {formData.contact_phone || '-'}{formData.contact_email ? ` · ${formData.contact_email}` : ''}</dd></div>
              </dl>
              <Field label="ตำแหน่ง" name="customer_signer_job_position">
                {text('customer_signer_job_position', { placeholder: 'เช่น กรรมการผู้จัดการ' })}
              </Field>
              <span className="rs-help">ชื่อและเบอร์โทรมาจากขั้นตอนข้อมูลลูกค้า ลูกค้าไม่ต้องมีบัญชี e-Contract</span>
            </div>
          </div>
          {errors.loa_detail && <div className="econ-field-error">{errors.loa_detail}</div>}

          <details className="rs-nested" open={!simpleApprover}>
            <summary>ปรับลำดับผู้ลงนามเอง (เพิ่มผู้อนุมัติ พยาน หรือเปลี่ยนลำดับ)</summary>
            <LoaEditor levels={loaLevels} onChange={(l) => { setLoaLevels(l); clearError('loa_detail'); }} formData={formData} econForm={econForm} locked={locked} />
          </details>

          <div className="rs-subhead">เอกสาร</div>
          <div className="rs-grid">
            <Field label="วันที่ทำสัญญา" name="contract_date">
              <input type="date" name="contract_date" value={econForm.contract_date || ''} onChange={onChange} disabled={locked} className={cls('contract_date')} />
            </Field>
            <Field label="เรื่อง" name="subject" full>
              {text('subject', { placeholder: 'สัญญาเช่าเครื่อง ...' })}
            </Field>
          </div>

          <div className="rs-subhead">การเงิน</div>
          <div className="rs-grid">
            <Field label="มูลค่าสินทรัพย์ให้เช่า" name="rental_asset_value">{money('rental_asset_value')}</Field>
            <Field label="ภาษีค่าเช่า" name="rental_vat_percent">{vatSelect('rental_vat_percent')}</Field>
            <Field label="ภาษีเงินมัดจำ" name="deposit_vat_percent">{vatSelect('deposit_vat_percent')}</Field>
            <Field label="ภาษีค่าขนส่ง" name="transport_vat_percent">{vatSelect('transport_vat_percent')}</Field>
            <Field label="วันที่เริ่มเช่า" name="rental_start_date"
              hint={`ถ้าไม่ระบุ ใช้วันที่เริ่มสัญญา (${formData.contract_start_date || '-'}) · สิ้นสุด: ${rentalEnd || '-'}`}>
              <input type="date" name="rental_start_date" value={econForm.rental_start_date || ''} onChange={onChange} disabled={locked} className={cls('rental_start_date')} />
            </Field>
            <Field label="จ่ายล่วงหน้าทุกวันที่" name="rental_advance_payment_schedule" hint="วันที่ของเดือน 1–31">
              {digits('rental_advance_payment_schedule', { maxLength: 2, placeholder: '1–31' })}
            </Field>
            <Field label="วันที่เริ่มจ่ายงวดแรก" name="rental_first_payment_date">
              <input type="date" name="rental_first_payment_date" value={econForm.rental_first_payment_date || ''} onChange={onChange} disabled={locked} className={cls('rental_first_payment_date')} />
            </Field>
          </div>

          <div className="rs-subhead">รอบการเข้าตรวจสอบเครื่อง</div>
          <label className="rs-toggle">
            <input type="checkbox" checked={Boolean(econForm.inspection_interval_is_text)} disabled={locked}
              onChange={(e) => setField('inspection_interval_is_text', e.target.checked)} />
            <span>ระบุเป็นข้อความ (เช่น "ตามที่ตกลงกัน") แทนการระบุตัวเลขและหน่วย</span>
          </label>
          {econForm.inspection_interval_is_text ? (
            <div className="rs-grid">
              <Field label="ข้อความรอบการตรวจสอบ" name="inspection_interval_text" required full>
                {text('inspection_interval_text', { maxLength: 255, placeholder: 'ตามที่ตกลงกัน' })}
              </Field>
            </div>
          ) : (
            <div className="rs-grid">
              <Field label="จำนวน" name="inspection_interval">
                {digits('inspection_interval', { maxLength: 3, placeholder: 'เช่น 6' })}
              </Field>
              <Field label="หน่วยรอบ" name="inspection_interval_unit">
                <select name="inspection_interval_unit" value={econForm.inspection_interval_unit} onChange={onChange} disabled={locked} className={cls('inspection_interval_unit')}>
                  {INSPECTION_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>
            </div>
          )}

          <div className="rs-subhead">สถานที่ติดตั้งและผู้ให้เช่า</div>
          <div className="rs-grid">
            <Field label="ชื่อสถานที่ติดตั้ง" name="installation_location_name" hint="มักไม่ใช่ชื่อเดียวกับชื่อนิติบุคคล เช่น ชื่อร้าน/สาขา">
              <div className="rs-inline">
                {text('installation_location_name', { placeholder: 'เช่น ร้าน BlueSpice & Herbs' })}
                <button type="button" disabled={locked} onClick={() => setField('installation_location_name', customerName)} className="rs-btn rs-btn--ghost rs-btn--sm">
                  ใช้ชื่อลูกค้า
                </button>
              </div>
            </Field>
          </div>
          <dl className="rs-facts">
            <div><dt>สถานที่ติดตั้ง</dt><dd>{payload.installation_address || '-'} <span className="rs-help">(แก้ไขได้ที่ขั้นตอนที่ 1)</span></dd></div>
            <div>
              <dt>ผู้ให้เช่า (Peerapat)
                <button type="button" className="econ-link-btn ms-2" onClick={() => setShowLessor(v => !v)}>{showLessor ? 'ซ่อน' : 'แก้ไข'}</button>
              </dt>
              {!showLessor && (
                <dd>
                  {econForm.lessor_company_name} · {econForm.lessor_company_phone_number} · {econForm.lessor_signer_full_name}
                  {econForm.lessor_signer_job_position ? ` (${econForm.lessor_signer_job_position})` : ''}
                  <br />{econForm.lessor_company_address}
                </dd>
              )}
            </div>
          </dl>
          {showLessor && (
            <div className="rs-grid">
              <Field label="บริษัทผู้ให้เช่า" name="lessor_company_name">{text('lessor_company_name')}</Field>
              <Field label="เบอร์โทรบริษัท" name="lessor_company_phone_number">{text('lessor_company_phone_number', { maxLength: 10 })}</Field>
              <Field label="ผู้ลงนาม Peerapat" name="lessor_signer_full_name">{text('lessor_signer_full_name')}</Field>
              <Field label="ตำแหน่งผู้ลงนาม" name="lessor_signer_job_position">{text('lessor_signer_job_position')}</Field>
              <Field label="ที่อยู่บริษัท" name="lessor_company_address" full>{text('lessor_company_address')}</Field>
            </div>
          )}

          <div className="rs-subhead">ข้อตกลงเพิ่มเติม (ข้อ 9 เป็นต้นไป)</div>
          <div data-econ-error={errors.additional_terms ? '1' : undefined}>
            <TermsEditor items={termItems} onChange={changeTerms} locked={locked} />
            {errors.additional_terms && <div className="econ-field-error">{errors.additional_terms}</div>}
          </div>
        </div>
      </details>

      <div className="rs-footbar">
        <button type="button" onClick={onBack} className="rs-btn rs-btn--ghost">
          <i className="bi bi-arrow-left"></i> {backLabel}
        </button>
        <div className="rs-footbar__right">
          <button type="button" onClick={saveDraft} disabled={savingDraft || locked} className="rs-btn rs-btn--ghost">
            {savingDraft ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-save"></i>}
            บันทึกร่าง
          </button>
          <button type="button" onClick={requestSend} className="rs-btn rs-btn--primary" disabled={generating || locked}>
            {generating ? (
              <><span className="spinner-border spinner-border-sm"></span>กำลังส่ง...</>
            ) : locked ? (
              <><i className="bi bi-lock-fill"></i>สัญญาถูกล็อก</>
            ) : (
              <><i className="bi bi-send-fill"></i>{isUpdate ? 'ตรวจสอบและอัปเดตสัญญา' : 'ตรวจสอบและส่งสัญญา'}</>
            )}
          </button>
        </div>
      </div>

      {reviewOpen && (
        <div className="rs-modal" role="dialog" aria-modal="true" aria-label="ตรวจสอบก่อนส่ง">
          <div className="rs-modal__panel">
            <div className="rs-card__kicker">REVIEW</div>
            <h2 className="rs-modal__title">ตรวจสอบก่อนส่งสัญญา</h2>
            <p className="rs-modal__lead">{isUpdate ? 'ข้อมูลนี้จะอัปเดตสัญญาเดิมบน e-Contract' : 'ข้อมูลนี้จะถูกส่งไปสร้างสัญญาบน e-Contract'}</p>
            <dl className="rs-review">
              <div><dt>เลขที่สัญญา</dt><dd className="rs-mono">{contractNumber || numberInfo.number || '—'}</dd></div>
              <div><dt>แบบสัญญา</dt><dd>{CONTRACT_TYPES.find(t => t.value === econForm.contract_type)?.labelTh || '—'}</dd></div>
              <div><dt>ชื่อสัญญา</dt><dd>{econForm.title || '—'}</dd></div>
              <div><dt>ผู้เช่า</dt><dd>{customerName || '—'}</dd></div>
              <div><dt>ระยะเวลา</dt><dd>{formData.contract_start_date || '—'} ถึง {formData.contract_end_date || '—'} ({formData.contract_duration} เดือน)</dd></div>
              <div><dt>ค่าเช่า / เดือน</dt><dd>฿{formatBaht(amounts.totalRental)} <span className="rs-help">รวม VAT</span></dd></div>
              <div><dt>เงินมัดจำ</dt><dd>฿{formatBaht(amounts.totalDeposit)}</dd></div>
              <div><dt>เครื่อง</dt><dd>{details.map(d => `${d.description || '-'} × ${d.quantity}${d.serial_number ? ` (${d.serial_number})` : ''}`).join(', ')}</dd></div>
              <div><dt>แผนก / POA</dt><dd>{econForm.department_code || '—'} · {econForm.poa_number || '—'}</dd></div>
              <div><dt>ผู้ลงนาม</dt><dd>{loaLevels.map(l => `${PARTY_LABEL[l.contract_party_type] || l.contract_party_type}: ${l.members.map(m => m.email || m.first_name || '-').join(', ')}`).join(' → ')}</dd></div>
            </dl>
            <div className="rs-modal__actions">
              <button type="button" className="rs-btn rs-btn--ghost" onClick={() => setReviewOpen(false)}>กลับไปแก้ไข</button>
              <button type="button" className="rs-btn rs-btn--primary" onClick={confirmSend}>
                <i className="bi bi-send-fill"></i>{isUpdate ? 'ยืนยันและอัปเดตสัญญา' : 'ยืนยันและส่งสัญญา'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Developer view: what would go to NetSuite (dry-run) and the raw e-Contract
          payload/log. Admins only — it is noise for everyone else. */}
      {isAdmin && (
        <details className="rs-log" open={generating || terminalLog.length > 0 || netsuiteLog.length > 0}>
          <summary><i className="bi bi-terminal"></i> บันทึกการเรียก API — request และ response</summary>
          <div className="rental-form__terminal">
            <div className="rental-form__terminal-titlebar">
              <span className="rental-form__terminal-dot rental-form__terminal-dot--red"></span>
              <span className="rental-form__terminal-dot rental-form__terminal-dot--yellow"></span>
              <span className="rental-form__terminal-dot rental-form__terminal-dot--green"></span>
              <span className="rental-form__terminal-title">generate.log</span>
            </div>
            <div className="rental-form__terminal-body">
              <div className="rental-form__terminal-divider">── NetSuite (dry-run) ──────────────────────────</div>
              {renderLog(netsuiteLog, '$ ยังไม่ได้กดสร้าง')}
              <div className="rental-form__terminal-divider">── e-Contract ───────────────────────────────────</div>
              <div className="rental-form__terminal-line rental-form__terminal-line--preview">
                <span className="rental-form__terminal-ts">[preview]</span>
                <span className="rental-form__terminal-msg">
                  {`payload ที่จะส่งถ้ากดปุ่มตอนนี้:\n${JSON.stringify(
                    { reference_id: isUpdate ? '<เดิม, ใช้ซ้ำ>' : '<generated server-side>', ...payload },
                    null,
                    2
                  )}`}
                </span>
              </div>
              {renderLog(terminalLog, '$ ยังไม่มีการส่ง')}
            </div>
          </div>
        </details>
      )}
    </div>
    </ErrorsContext.Provider>
  );
};

export default EContractSection;
